import { useQueryClient } from '@tanstack/react-query'
import { addDays, format, isSameMonth, startOfMonth, subDays } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { globalQueryKeys } from '@opallabs/api'
import { allMonthCalendarQueries, readMonthCalendar } from '@opallabs/api/BFF'
import { type IETFLanguageTag, type WeekStartsOn } from '@opallabs/concepts'
import {
  applyCalendarFilters,
  type CalendarFilterCategory,
  type CalendarFilterState,
  CalendarFilterCategoryIds,
  type CalendarValueTrees,
  CalendarFilterDrawer,
  calendarFiltersForKinds,
  calendarMatchingCategories,
  countByKind,
  countCalendarFilters,
  deserializeCalendarFilters,
  listedByName,
  serializeCalendarFilters,
  updateCalendarFilterParams,
  useCalendarFilters,
  useWeekViewFilterSeed,
  useWorkspaceFilterOptions,
  visibleCalendarFilterCategories,
} from '@opallabs/filters'
import { MessageKeys, useMessage, useWorkspace } from '@opallabs/hooks'
import { Button, ButtonSize, Icon, Tooltip } from '@opallabs/lapidary'
import { CalendarPanel } from '../CalendarPanel'
import { buildDayItems, buildOngoingEntries } from '../CalendarPanel/panelData'
import {
  isCalendarPanelShowing,
  useCalendarPanel,
} from '../CalendarPanel/useCalendarPanel'
import { type CalendarDateRange } from '../create'
import { MonthCalendarGrid } from '../MonthCalendarGrid'
import { buildMonthGrid, makeDayKey } from '../monthGrid'
import { useEditCalendarItem } from '../mutations/useEditCalendarItem'
import { useRescheduleCalendarItem } from '../mutations/useRescheduleCalendarItem'
import {
  FALLBACK_SEGMENT_COLOR,
  renderBreakdownTooltipContent,
} from '../tooltip/breakdownTooltip'
import { type CalendarItem, type ResolveImageUrl } from '../types'
import {
  hasOwnCalendarFilters,
  initialCalendarFilters,
  writeStoredCalendarFilters,
} from './filterPersistence'
import {
  monthHashRoute,
  monthReturnRoute,
  parseFilterHash,
  parseMonthHash,
  parseQuickfindHash,
} from './monthHash'
import { monthLoadingState } from './monthLoadingState'
import { MonthToolbar } from './MonthToolbar'
import * as styles from './styles'
import { toCalendarItems } from './toCalendarItems'
import {
  useCalendarItemActions,
  type CalendarHostActions,
} from './useCalendarItemActions'
import { useCreateOnDay } from './useCreateOnDay'
import {
  breakdownFallback,
  NO_GROUP_ID,
  toBreakdownDimension,
  useDayBreakdown,
} from './useDayBreakdown'
import { useMomentCovers } from './useMomentCovers'
import { useMonthViewSettings } from './useMonthViewSettings'
import { usePanelSnapshot, usePrefetchSnapshot } from './usePanelSnapshot'
import { useWorkflowSummaries } from './useWorkflowSummaries'
import {
  buildColorLookup,
  buildLabelSetColors,
  buildLabelSets,
  buildNameLookups,
  useWorkspaceDisplay,
} from './useWorkspaceDisplay'

export { selectors as workspaceMonthCalendarSelectors } from './styles'

/**
 * What a filter state reads as in the address, in one settled order, so two
 * states can be compared however either of them was built.
 */
const filterFingerprint = (state: CalendarFilterState): string => {
  const params = serializeCalendarFilters(state)
  params.sort()
  return params.toString()
}

/**
 * How long the pointer rests on a chip before its snapshot is read, so sweeping
 * across a busy month reads only the chip it stops on.
 */
const HOVER_PREFETCH_DELAY_MS = 150

/** A week nobody filtered, and the identity a memo can lean on. */
const NO_WEEK_FILTERS: readonly string[] = []
const NO_WEEK_FILTER_DATES: Readonly<Record<string, string>> = {}

export type WorkspaceMonthCalendarProps = Readonly<{
  /**
   * Whether this reader may make something on a day of the calendar. Without
   * it the day cells and the day panel offer no way to.
   *
   * @default true
   */
  canCreate?: boolean
  /**
   * Whether this reader may change what the month is showing. Without it
   * nothing can be dragged, the right-click menu offers only the ways to read
   * an item, and the panel's fields are there to be read.
   *
   * @default true
   */
  canEdit?: boolean
  className?: string
  /**
   * The menu actions the page around the calendar owns, each of which opens a
   * modal of the host's own. What the host leaves out — because it cannot do it,
   * or because a feature flag says this workspace may not — is left off the
   * menu.
   */
  hostActions?: CalendarHostActions
  /**
   * The month to open on, for a person coming from another view. The iframe's
   * hash still names the last month drawn, so this one wins over it. Leave it
   * out and the month opens on what the hash names, or on this month.
   */
  initialMonth?: Date
  /**
   * True while the moment or content overlay is open over this same page.
   * Nothing else says the month is being read again: the overlay is drawn in
   * this window, so the window never loses focus and a refetch on focus would
   * never fire.
   */
  isComposerOpen?: boolean
  locale?: IETFLanguageTag
  /**
   * Opens the item itself, which only the host knows how to route to.
   * `returnTo` is the address of the month the person is leaving, for the host
   * to hand to whatever it opens.
   */
  onNavigate: (item: CalendarItem, options: { returnTo?: string }) => void
  /**
   * Hands the host the keyword the parent frame's address names, when Back,
   * Forward or a pasted link moves it to another one. The host owns the box
   * the keyword is typed into, so only the host can show it there.
   */
  onQuickfindChange?: (quickfind: string) => void
  /**
   * Hands the host what the month is filtered by, each time it changes. The
   * trees say which value sits under which, so the host can read a label set
   * or a channel as the rows under it.
   */
  onFilterStateChange?: (
    filters: CalendarFilterState,
    trees: CalendarValueTrees
  ) => void
  /**
   * The first and last day the grid is drawing. The grid draws whole weeks, so
   * these reach into the months either side of the one on show.
   */
  onVisibleRangeChange?: (start: Date, end: Date) => void
  /**
   * What to search the grid for. Quickfind dims what it does not match; the
   * host owns the box it is typed into. It goes into the parent frame's address
   * beside the filters, so a link opens on the same search.
   */
  quickfind?: string
  /**
   * The days the story runs for. Days outside it cannot hold anything new, the
   * rule the story's own calendar has always had.
   */
  storyDateRange?: CalendarDateRange
  /**
   * Narrows the calendar to one story, for the story's own page. The story
   * filter is dropped when this is set: everything on screen is already in it.
   */
  storyId?: string
  /**
   * The same story's v2 primary key, which is the id its page is addressed by.
   * A month drawn inside a story comes back to the story's page, not to the
   * workspace's.
   */
  storyLegacyId?: string
  /**
   * The day each end of the week view's own live window is set to, by the id
   * of the row that switches it on (`duration_starts_at`, `duration_ends_at`).
   * The tray keeps a date for both ends whether or not either is switched on,
   * so `weekFilters` says which of them count.
   */
  weekFilterDates?: Readonly<Record<string, string>>
  /**
   * The week view's saved filters, as the shared filter bar's own value ids
   * (`service-3`, `account-12`, `label-45`, `status-approved`). A month with
   * nothing filtered of its own starts from them, once, and never writes
   * anything back.
   */
  weekFilters?: readonly string[]
  weekStartsOn: WeekStartsOn
}>

/**
 * The month view of the workspace calendar.
 *
 * This is the only part of `@opallabs/calendar` that knows where the data comes
 * from. It reads one month window from the BFF, turns it into the shape the
 * grid draws, and wires the toolbar, panel and filter drawer together.
 *
 * It mounts inside the monolith's iframe, where there is no router, so the
 * month and the filters are read from and written to the address directly
 * rather than through one.
 *
 * ## Where the filters live
 *
 * Three places, and each answers a question the others cannot:
 *
 * | Place | What it is for |
 * | --- | --- |
 * | The iframe's query string | What the drawer is editing now |
 * | The parent frame's hash | Reloads, and a link someone can send |
 * | `localStorage`, per workspace | Coming back tomorrow with no link to follow |
 *
 * The iframe is rebuilt from the parent's address, so its own query string does
 * not survive a reload; the hash does. On the way in the address wins over
 * storage — a shared link must show the reader what the sender saw.
 *
 * The quickfind keyword lives in the parent's hash alone. It is a search, not a
 * filter: a link carries it, and coming back tomorrow does not.
 *
 * ## Every request the month view makes
 *
 * | Request | When |
 * | --- | --- |
 * | `asgard-bff/…/calendar/month` | At mount, and on each month change |
 * | `services/v2` | At mount |
 * | `label_sets/v2` | When the panel first opens, or when grouping by label |
 * | `stories/v2` | When the panel first opens |
 * | `boards/v3` | When the panel first opens |
 * | `planning_statuses/v3` | When the panel first opens |
 * | `moments/v3` | With the month, for the legacy ids the workflows are keyed by; and when the panel opens on a day or a moment, for their cover pictures |
 * | `workflows/v2/workflows` | With the month, batched a hundred moments at a time |
 * | `stamps/v2` | When a create form first opens, then paged to the end |
 * | `content/v3/:id` | When the panel opens on a content item |
 *
 * One `services/v2` answers three questions — the channel mark on a chip, the
 * channel and account filter options, and the create form's channel list — so
 * all three read it through `useReadServices`, which keys them together. Adding
 * a fourth reader under a key of its own fetches the endpoint twice.
 *
 * The panel's lists wait because nothing on the grid reads them, and a workspace
 * can hold thousands of stories. Channels cannot wait: a chip names its account
 * and quickfind searches those names. Once fetched, a list is cached, so
 * closing and reopening the panel asks for nothing.
 *
 * A moment's workflow takes two requests because no endpoint joins them:
 * `/workflows/v2` knows a moment by its v2 primary key alone, so the moments
 * are asked for their legacy ids first. A `/v3/workflows` is specified but not
 * yet served; when it is, both requests collapse into one.
 *
 * The page around the calendar makes requests of its own — the shell's
 * `moments/:slug/services` and `api/v1/users` among them. They are the Backbone
 * shell's, not this view's, and turning the month view off does not stop them.
 */
export const WorkspaceMonthCalendar: React.FC<WorkspaceMonthCalendarProps> = ({
  canCreate = true,
  canEdit = true,
  className,
  hostActions,
  initialMonth,
  isComposerOpen = false,
  locale,
  onFilterStateChange,
  onNavigate,
  onQuickfindChange,
  onVisibleRangeChange,
  quickfind = '',
  storyDateRange,
  storyId,
  storyLegacyId,
  weekFilterDates = NO_WEEK_FILTER_DATES,
  weekFilters = NO_WEEK_FILTERS,
  weekStartsOn,
}) => {
  const workspace = useWorkspace()
  const queryClient = useQueryClient()

  const [month, setMonth] = useState(
    () => initialMonth ?? parseMonthHash(window.location.hash) ?? new Date()
  )
  const [isTruncationDismissed, setTruncationDismissed] = useState(false)
  // The view sheet offers the workspace's label sets, and nothing else on a
  // month that is not coloured by one has ever asked for them.
  const [wasViewMenuOpened, setViewMenuOpened] = useState(false)

  const { settings, update: changeSettings } = useMonthViewSettings(
    workspace.uuid
  )

  // The grid draws whole weeks, so the window reaches into the months on either
  // side. Asking the grid for its own days keeps the fetch and the drawing from
  // ever disagreeing about where the month ends.
  const [firstDay, lastDay] = useMemo(() => {
    const days = buildMonthGrid({ month, today: month, weekStartsOn }).flatMap(
      (week) => week.days
    )
    return [days[0].date, days[days.length - 1].date]
  }, [month, weekStartsOn])
  // The fetch reaches a day past the grid at each end, the way the week view's
  // does. The server files content under its own zone, and the grid draws it on
  // the day the reader is in, which can be the day either side. Anything that
  // still lands outside the grid is simply not drawn.
  const startDate = makeDayKey(subDays(firstDay, 1))
  const endDate = makeDayKey(addDays(lastDay, 1))

  useEffect(() => {
    onVisibleRangeChange?.(firstDay, lastDay)
  }, [firstDay, lastDay, onVisibleRangeChange])

  const { data, isFetching, isPreviousData } = readMonthCalendar.useQuery({
    endDate,
    startDate,
    storyId,
    workspaceUuid: workspace.uuid,
  })

  // The same window the query above asked for, so an optimistic change is
  // written into the cache entry the grid is drawing from.
  const queryIdentifiers = useMemo(
    () => ({
      queryParams: {
        filter: {
          scheduled_at: { gteq: startDate, lteq: endDate },
          ...(storyId === undefined ? {} : { story_id: storyId }),
        },
      },
      workspaceUuid: workspace.uuid,
    }),
    [endDate, startDate, storyId, workspace.uuid]
  )

  const reschedule = useRescheduleCalendarItem({
    identifiers: queryIdentifiers,
    locale,
  })

  const itemActions = useCalendarItemActions({
    canEdit,
    hostActions,
    workspaceUuid: workspace.uuid,
  })

  const menu = useMemo(
    () => ({
      capabilities: itemActions.capabilities,
      onItemAction: itemActions.onItemAction,
    }),
    [itemActions.capabilities, itemActions.onItemAction]
  )

  const panel = useCalendarPanel()
  const isPanelOpen = panel.panel !== null

  const initialState = useMemo(
    () =>
      initialCalendarFilters({
        hash: window.location.hash,
        search: window.location.search,
        workspaceUuid: workspace.uuid,
      }),
    [workspace.uuid]
  )

  // Nothing in the address, and this workspace's month has never been filtered
  // — not even filtered and then cleared, which is a month somebody meant to
  // leave open. It is decided once, at the first render, because the month
  // records filters of its own as soon as it takes any over.
  const [hasNoFiltersOfItsOwn] = useState(
    () =>
      countCalendarFilters(initialState) === 0 &&
      !hasOwnCalendarFilters(workspace.uuid)
  )
  const hasWeekLabelFilters = weekFilters.some((id) => id.startsWith('label-'))

  // A label filter does not only arrive at the first render: Back, Forward and
  // a pasted address all bring one later, and the labels have to be read for
  // that one too.
  const [isLabelFiltered, setLabelFiltered] = useState(
    () => initialState.cats[CalendarFilterCategoryIds.label] !== undefined
  )

  const {
    categories: allCategories,
    isLoading: areOptionsLoading,
    legacyFilterValues,
    trees,
  } = useWorkspaceFilterOptions({
    areNamedListsWanted: isPanelOpen,
    // A label set filters through the label tree, so a month opened with one
    // in its address would hide everything until the labels arrived — and with
    // the panel shut they never would. A month coloured by a set needs the
    // labels for the colours themselves, and the view sheet needs them to
    // offer the sets at all.
    isLabelListWanted:
      settings.sortBy === 'label' ||
      settings.colorLabelSetId !== undefined ||
      wasViewMenuOpened ||
      isLabelFiltered ||
      // A label the week view filters by can only be taken over once the
      // labels themselves are here to name it.
      (hasNoFiltersOfItsOwn && hasWeekLabelFilters),
    workspaceId: workspace.id,
    workspaceUuid: workspace.uuid,
  })

  // Inside one story, filtering by story can only ever hide everything.
  const isOffered = useCallback(
    (category: CalendarFilterCategory) =>
      !(
        storyId !== undefined && category.id === CalendarFilterCategoryIds.story
      ),
    [storyId]
  )

  const categories = useMemo(
    () =>
      listedByName(
        visibleCalendarFilterCategories(
          allCategories,
          settings.shownKinds
        ).filter(isOffered)
      ),
    [allCategories, isOffered, settings.shownKinds]
  )

  const names = useMemo(() => buildNameLookups(allCategories), [allCategories])
  const colors = useMemo(() => buildColorLookup(allCategories), [allCategories])
  const labelSets = useMemo(
    () => buildLabelSets(allCategories),
    [allCategories]
  )
  const labelSetColors = useMemo(
    () => buildLabelSetColors(allCategories, settings.colorLabelSetId),
    [allCategories, settings.colorLabelSetId]
  )

  const items = useMemo(
    () =>
      data === undefined
        ? []
        : toCalendarItems(data, {
            locale,
            resolveAccountName: (id) => names.account.get(id),
            workspaceSlug: workspace.slug,
          }),
    [data, locale, names, workspace.slug]
  )

  // The grid marks every moment and every piece of content with the state of
  // the workflow over it, so the workflows are read with the month.
  const workflowSummaries = useWorkflowSummaries({
    areItemsForWindow: !isPreviousData,
    items,
    window: `${workspace.uuid}:${startDate}:${endDate}:${storyId ?? ''}`,
  })

  const {
    renderChannelIcon,
    renderOptionIcon,
    renderStatusIcon,
    resolveChosenColor,
    resolveColor,
    resolveDisplay,
  } = useWorkspaceDisplay({
    colors,
    items,
    labelSetColors,
    names,
    shouldFetchPlanningStatuses: isPanelOpen,
    workflowSummaries,
    workspaceId: workspace.id,
  })

  // True only while the grid is being moved by the parent frame's own address.
  const isFollowingAddress = useRef(false)

  /**
   * The address a person can copy belongs to the parent frame, so the month,
   * the filters and the keyword are announced to it rather than written here.
   *
   * `shouldAddEntry` says what the parent frame does with its history. Moving
   * to another month earns an entry of its own, so Back walks a person out the
   * way they came. Everything else writes over the entry they are standing on:
   * the drawer applies a filter on every tick, and ten ticks must not cost ten
   * presses of Back.
   */
  const announce = useCallback(
    (
      month: Date,
      filters: CalendarFilterState,
      { shouldAddEntry }: { shouldAddEntry: boolean }
    ) => {
      // Following the address is not a new address. Writing it back makes the
      // parent frame answer its own message, and it costs a person their
      // Forward as soon as that write pushes an entry.
      if (isFollowingAddress.current) return

      parent.postMessage(
        {
          message: MessageKeys.execute,
          replace: !shouldAddEntry,
          route: monthHashRoute(month, filters, quickfind),
        },
        '*'
      )
    },
    [quickfind]
  )

  const onFiltersChange = useCallback(
    (state: CalendarFilterState) => {
      const search = updateCalendarFilterParams(
        new URLSearchParams(window.location.search),
        state
      ).toString()

      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${search === '' ? '' : `?${search}`}${
          window.location.hash
        }`
      )

      // The iframe's own address dies with the iframe. The parent's hash is
      // what a reload is rebuilt from, and storage is what a person comes back
      // to when they open the calendar again with no link to follow.
      announce(month, state, { shouldAddEntry: false })
      writeStoredCalendarFilters(workspace.uuid, state)
    },
    [announce, month, workspace.uuid]
  )

  const filters = useCalendarFilters({
    // Every category as it is written, so the count can tell a value that is
    // filtering from a moment status being kept while the moment row is off.
    categories: allCategories,
    initialState,
    onChange: onFiltersChange,
    shownKinds: settings.shownKinds,
  })

  useEffect(() => {
    onFilterStateChange?.(filters.state, trees)
  }, [filters.state, onFilterStateChange, trees])

  useWeekViewFilterSeed({
    areValuesReady: !areOptionsLoading,
    dates: weekFilterDates,
    hasFiltersNow: filters.count > 0,
    isWanted: hasNoFiltersOfItsOwn,
    legacyIds: weekFilters,
    onSeed: filters.replaceAll,
    values: legacyFilterValues,
  })

  const { renderDayCreate, renderPanelCreate } = useCreateOnDay({
    getClearFilters: () => (filters.count > 0 ? filters.clearAll : null),
    locale,
    storyDateRange,
    storyId,
    workspaceId: workspace.id,
    workspaceUuid: workspace.uuid,
  })

  const dayFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'long',
        weekday: 'long',
      }),
    [locale]
  )

  const onPreviewRange = hostActions?.onPreviewRange
  const renderDayActions = useCallback(
    (date: Date) => (
      <>
        {onPreviewRange !== undefined && (
          <Tooltip content="See in-market preview">
            <Button
              ariaLabel={`See the in-market preview for ${dayFormat.format(
                date
              )}`}
              icon={<Icon ariaHidden={true} glyph="gem-eye" />}
              name="calendar-day-preview"
              onClick={() => onPreviewRange(date, date)}
              // Small keeps the hover fill inside the 26px day header.
              size={ButtonSize.Small}
              variant="ghost"
            />
          </Tooltip>
        )}
        {canCreate && renderDayCreate(date)}
      </>
    ),
    [canCreate, dayFormat, onPreviewRange, renderDayCreate]
  )

  // Filtering runs on the categories as they are written, not as they were
  // fetched. A category with no options is left out of the drawer, and a filter
  // restored from the address would otherwise hide nothing at all until its
  // option list arrived — or, now that the lists wait for the panel, never.
  const allMatchingCategories = useMemo(
    () => calendarMatchingCategories({ workflowSummaries }),
    [workflowSummaries]
  )

  const matchingCategories = useMemo(
    () =>
      visibleCalendarFilterCategories(
        allMatchingCategories,
        settings.shownKinds
      ).filter(isOffered),
    [allMatchingCategories, isOffered, settings.shownKinds]
  )

  // A moment status chosen before the moment row was turned off stays in the
  // address, and says nothing until the row comes back: the drawer no longer
  // offers it, and a filter a person cannot see must not empty the calendar.
  const matchingState = useMemo(
    () =>
      calendarFiltersForKinds(
        filters.state,
        allMatchingCategories,
        settings.shownKinds
      ),
    [allMatchingCategories, filters.state, settings.shownKinds]
  )

  // The Show set is applied after the filters, never inside them: turning the
  // moment layer off says "do not draw moments", not "these moments are
  // filtered out", so it must not take their content with it.
  const filteredItems = useMemo(
    () => applyCalendarFilters(items, matchingState, matchingCategories, trees),
    [items, matchingCategories, matchingState, trees]
  )

  const visibleItems = useMemo(
    () =>
      filteredItems.filter((item) => settings.shownKinds.includes(item.kind)),
    [filteredItems, settings.shownKinds]
  )

  // The panel saves a field as soon as a person leaves it. It needs every item
  // the month holds, hidden or not: content cannot go up before the moment it
  // belongs to, and only the moment says when that is.
  const editItem = useEditCalendarItem({
    identifiers: queryIdentifiers,
    items,
    locale,
  })

  // What the drawer's footer says back to a person: the calendar they are
  // looking at, not the month behind it. A truncated window is counted as it
  // arrived, because that is what the grid is drawing.
  const resultCounts = useMemo(() => countByKind(visibleItems), [visibleItems])

  // Being asked for is not the same as being on screen: the panel draws nothing,
  // and takes no width, when the item its panel was opened for has been filtered
  // or hidden away. So this follows what the panel draws, not what it was told.
  const isPanelShowing = isCalendarPanelShowing(panel.panel, {
    hasFilters: true,
    items: visibleItems,
  })

  // Asking for a day already on show shuts the panel, so the one control both
  // opens the details and puts them away.
  const togglePanel = useCallback(() => {
    if (isPanelShowing) {
      panel.closePanel()
      return
    }
    const today = new Date()
    panel.openDay(isSameMonth(today, month) ? today : startOfMonth(month))
  }, [isPanelShowing, month, panel])

  // The item panel draws whichever of these it names, so the picture is
  // fetched for the same item the panel will find — never for one it has
  // already filtered away.
  const openItemId = panel.panel?.type === 'item' ? panel.panel.id : undefined
  const shownItem = visibleItems.find((item) => item.id === openItemId)
  const resolveSnapshot = usePanelSnapshot(shownItem)

  // The covers of the moments the panel lists, worked out the way the day
  // panel lists them, and of the moment it has open. Nothing else in the month
  // draws one.
  const coverMomentIds = useMemo(() => {
    if (panel.panel?.type !== 'day')
      return shownItem?.kind === 'moment' ? [shownItem.id] : []

    const { date } = panel.panel
    return [
      ...buildOngoingEntries(visibleItems, date, {
        shouldShowInMarketBars: settings.shouldShowContentDuration,
      }).map((entry) => entry.item),
      ...buildDayItems(visibleItems, date),
    ].flatMap((item) => (item.kind === 'moment' ? [item.id] : []))
  }, [panel.panel, settings.shouldShowContentDuration, shownItem, visibleItems])
  const resolveMomentCover = useMomentCovers(coverMomentIds)

  const prefetchSnapshot = usePrefetchSnapshot()
  const hoverTimer = useRef<ReturnType<typeof setTimeout>>()
  const prefetchHoveredItem = useCallback(
    (itemId: string | undefined) => {
      clearTimeout(hoverTimer.current)
      if (itemId === undefined) return

      hoverTimer.current = setTimeout(
        () => prefetchSnapshot(visibleItems.find((item) => item.id === itemId)),
        HOVER_PREFETCH_DELAY_MS
      )
    },
    [prefetchSnapshot, visibleItems]
  )
  useEffect(() => () => clearTimeout(hoverTimer.current), [])

  const resolveImageUrl = useCallback<ResolveImageUrl>(
    (item) =>
      item.kind === 'moment' ? resolveMomentCover(item) : resolveSnapshot(item),
    [resolveMomentCover, resolveSnapshot]
  )

  // A day panel lists what the loaded month holds on that day, and an item
  // panel leads back out to one. Another month holds neither, so both shut.
  // The filter drawer is about every month, so it stays.
  const isFilterDrawerOpen = panel.panel?.type === 'filters'
  const { closePanel } = panel
  const closeMonthPanel = useCallback(() => {
    if (!isFilterDrawerOpen) closePanel()
  }, [closePanel, isFilterDrawerOpen])

  const changeMonth = useCallback(
    (next: Date) => {
      // Today, and any day the date picker offers, can land on the month
      // already on screen. That is not a move: writing the same address again
      // costs a person a press of Back that takes them nowhere.
      if (isSameMonth(next, month)) return

      setMonth(next)
      // Dismissing the banner answers for the month it was shown on. What the
      // next month is holding back is news the person has not heard yet.
      setTruncationDismissed(false)
      closeMonthPanel()
      announce(next, filters.state, { shouldAddEntry: true })
    },
    [announce, closeMonthPanel, filters.state, month]
  )

  // The keyword the address carries now. A keyword typed into the box writes
  // over the entry a person is standing on, the way a filter does: every pause
  // in typing is a new keyword, and none of them is somewhere to go Back to.
  const announcedQuickfind = useRef(parseQuickfindHash(window.location.hash))
  useEffect(() => {
    const keyword = quickfind.trim()
    if (keyword === announcedQuickfind.current) return

    announcedQuickfind.current = keyword
    announce(month, filters.state, { shouldAddEntry: false })
  }, [announce, filters.state, month, quickfind])

  /**
   * The month, the filters and the keyword the parent frame's address names,
   * taken as read.
   *
   * The whole of the address counts, so a hash with no filters in it is a month
   * somebody cleared rather than a month with nothing to say about them, and a
   * hash with no keyword is a month nobody is searching.
   */
  const { replaceAll: replaceAllFilters } = filters
  const followAddress = useCallback(() => {
    const hash = parent.location.hash
    const asked = parseMonthHash(hash)
    // `#day` and `#week` name the other views, and a hash the month never wrote
    // names none of them. Either way the month says nothing.
    if (asked === undefined) return

    const askedFilters = deserializeCalendarFilters(parseFilterHash(hash))
    if (askedFilters.cats[CalendarFilterCategoryIds.label] !== undefined)
      setLabelFiltered(true)

    isFollowingAddress.current = true
    try {
      if (!isSameMonth(asked, month)) {
        setMonth(asked)
        // The banner was dismissed for the month it was shown on. What this
        // one is holding back is news the person has not heard yet.
        setTruncationDismissed(false)
        closeMonthPanel()
      }
      if (filterFingerprint(askedFilters) !== filterFingerprint(filters.state))
        replaceAllFilters(askedFilters)
    } finally {
      isFollowingAddress.current = false
    }

    // Noted before the host is told, so the keyword it hands back is not
    // written to the address that named it.
    const askedQuickfind = parseQuickfindHash(hash)
    if (askedQuickfind !== quickfind.trim()) {
      announcedQuickfind.current = askedQuickfind
      onQuickfindChange?.(askedQuickfind)
    }
  }, [
    closeMonthPanel,
    filters.state,
    month,
    onQuickfindChange,
    quickfind,
    replaceAllFilters,
  ])

  // Back, Forward and a hand-typed address all move the parent frame's hash
  // where it stands, and the iframe is only ever rebuilt from it on a reload.
  // Without this the grid would keep the month it was built with while the
  // address bar named another one.
  useEffect(() => {
    parent.addEventListener('hashchange', followAddress)
    return () => parent.removeEventListener('hashchange', followAddress)
  }, [followAddress])

  // Week and Day take the month down, and coming back draws it again from the
  // iframe's own hash, so the hash follows the month wherever it goes. It is
  // written over, never added to: Back and Forward walk the parent's history.
  useEffect(() => {
    const route = monthHashRoute(month, filters.state, quickfind)
    if (window.location.hash === route) return

    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${route}`
    )
  }, [filters.state, month, quickfind])

  // Coming back from Week or Day leaves the parent frame on a bare `#month`
  // while the grid shows a month of its own, so the parent is told the month
  // once. It writes over the entry: the way here already made one.
  const isParentMonthUnnamed = useRef(
    parseMonthHash(parent.location.hash) === undefined
  )
  useEffect(() => {
    if (!isParentMonthUnnamed.current) return

    isParentMonthUnnamed.current = false
    announce(month, filters.state, { shouldAddEntry: false })
  }, [announce, filters.state, month])

  // The composer opens over this same page and hands an address back to the
  // parent frame as it closes. Without the month in that address the parent
  // keeps the bare workspace page, so a reload after the round trip lands on
  // today rather than on the month the person was reading.
  const openItem = useCallback(
    (item: CalendarItem) =>
      onNavigate(item, {
        returnTo: monthReturnRoute(workspace.slug, month, filters.state, {
          quickfind,
          storyLegacyId,
        }),
      }),
    [filters.state, month, onNavigate, quickfind, storyLegacyId, workspace.slug]
  )

  // Everything the month draws, asked for again. Only the window is keyed, so
  // every month this workspace has loaded is asked again — and the workflow
  // summaries with them, because they are keyed by window alone and a moment
  // that has just arrived has no answer under it. Every cover held goes stale,
  // and only the ones on show are asked again now. Snapshots are keyed by
  // item, so only the ones of this month's items are asked again.
  const refreshMonth = useCallback(() => {
    allMonthCalendarQueries.invalidateCache(queryClient, {
      workspaceUuid: workspace.uuid,
    })
    queryClient.invalidateQueries({
      queryKey: globalQueryKeys.workflowsByMoments(),
    })
    queryClient.invalidateQueries({
      queryKey: globalQueryKeys.momentCovers(),
    })
    items.forEach((item) => {
      if (item.kind === 'content')
        queryClient.invalidateQueries({
          queryKey: globalQueryKeys.contentByIdV3(item.id),
        })
    })
  }, [items, queryClient, workspace.uuid])

  // Something outside the month changed a moment: Gem, or a modal or shroud
  // the monolith opened over this page.
  useMessage(MessageKeys.refreshMoments, refreshMonth)

  // Whatever a person did in the composer, the month behind it is now out of
  // date. Ask again the moment it closes.
  const wasComposerOpen = useRef(isComposerOpen)
  useEffect(() => {
    const hadBeenOpen = wasComposerOpen.current
    wasComposerOpen.current = isComposerOpen
    if (!hadBeenOpen || isComposerOpen) return

    refreshMonth()
  }, [isComposerOpen, refreshMonth])

  const dimension = toBreakdownDimension(settings.sortBy)
  const breakdownByDay = useDayBreakdown(visibleItems, dimension)

  // The same summary the week view's histogram gives a day, split by whatever
  // the month is sorted by. A month sorted by nothing still splits by channel.
  const dayTooltip = useCallback(
    (date: Date) => {
      const summary = breakdownByDay.get(makeDayKey(date))

      return renderBreakdownTooltipContent({
        breakdown: summary?.breakdown,
        colorFor: (groupId) => resolveColor(groupId) ?? FALLBACK_SEGMENT_COLOR,
        heading: format(date, 'EEE, MMM d'),
        isContentMode: true,
        nameFor: (groupId) =>
          groupId === NO_GROUP_ID
            ? breakdownFallback[dimension]
            : (resolveDisplay(dimension, groupId) ??
              breakdownFallback[dimension]),
        totalCount: summary?.total ?? 0,
      })
    },
    [breakdownByDay, dimension, resolveColor, resolveDisplay]
  )

  // `shownKinds` decides which items reach the grid, not how it draws them, so
  // it is the one setting the display does not carry.
  const display = useMemo(
    () => ({
      density: settings.contentDensity,
      namePriority: settings.namePriority,
      quickfind,
      shouldShowInMarketBars: settings.shouldShowContentDuration,
      shouldShowContentName: settings.shouldShowContentName,
      shouldShowLabelColor: settings.shouldShowContentLabelColor,
      shouldShowMomentBadge: settings.shouldShowMomentBadge,
      shouldShowTime: settings.shouldShowTime,
      sortBy: settings.sortBy,
      weeksMode: settings.weeksMode,
    }),
    [quickfind, settings]
  )

  const isTruncated =
    data?.data.meta.truncated === true && !isTruncationDismissed

  return (
    <div className={className} css={styles.root}>
      <MonthToolbar
        filterCount={filters.count}
        isFilterOpen={panel.panel?.type === 'filters'}
        isPanelShowing={isPanelShowing}
        locale={locale}
        month={month}
        onMonthChange={changeMonth}
        onSettingsChange={changeSettings}
        onToday={() => changeMonth(new Date())}
        onToggleFilters={panel.toggleFilters}
        labelSets={labelSets}
        onTogglePanel={togglePanel}
        onViewMenuOpen={() => setViewMenuOpened(true)}
        settings={settings}
        weekStartsOn={weekStartsOn}
      />

      <div className={styles.selectors.contents} css={styles.contents}>
        <div className={styles.selectors.grid} css={styles.grid}>
          {isTruncated && (
            <div
              className={styles.selectors.banner}
              css={styles.banner}
              role="status"
            >
              {/*
                Filters cannot help here: they run in the browser, over the
                items this request already left behind. Only a smaller ask can.
              */}
              <span>
                This month holds more than the calendar can show. Look at a
                shorter timescale, or at one story, to see everything.
              </span>
              <Button
                buttonText="Dismiss"
                name="dismiss-truncation"
                onClick={() => setTruncationDismissed(true)}
                size={ButtonSize.Small}
                variant="tertiary"
              />
            </div>
          )}

          <MonthCalendarGrid
            dayTooltip={dayTooltip}
            display={display}
            items={visibleItems}
            referenceItems={filteredItems}
            loading={monthLoadingState({
              hasWindow: data !== undefined,
              isFetching,
            })}
            locale={locale}
            menu={menu}
            month={month}
            onDayOpen={panel.openDay}
            onItemHover={prefetchHoveredItem}
            openItemId={openItemId}
            onEscape={panel.closePanel}
            onItemClick={(item) => panel.openItem(item.id)}
            onItemDrop={canEdit ? reschedule : undefined}
            onOverflowClick={panel.openDay}
            renderChannelIcon={renderChannelIcon}
            renderDayActions={renderDayActions}
            // The grid asks its host whether an item has earned a workflow
            // mark, so a host that has been told not to show them says no.
            renderStatusIcon={
              settings.shouldShowWorkflow ? renderStatusIcon : undefined
            }
            resolveChosenColor={resolveChosenColor}
            resolveDisplay={resolveDisplay}
            weekStartsOn={weekStartsOn}
          />
        </div>

        <CalendarPanel
          canEdit={canEdit}
          filtersSlot={
            <CalendarFilterDrawer
              categories={categories}
              filters={filters}
              isLoading={areOptionsLoading}
              locale={locale}
              onClose={panel.closePanel}
              renderOptionIcon={renderOptionIcon}
              resultCounts={resultCounts}
              shownKinds={settings.shownKinds}
              weekStartsOn={weekStartsOn}
            />
          }
          items={visibleItems}
          locale={locale}
          onClose={panel.closePanel}
          onEdit={editItem}
          onNavigate={openItem}
          onOpenDay={panel.openDay}
          onOpenItem={panel.openItem}
          panel={panel.panel}
          referenceItems={items}
          renderChannelIcon={renderChannelIcon}
          renderCreate={canCreate ? renderPanelCreate : undefined}
          renderStatusIcon={
            settings.shouldShowWorkflow ? renderStatusIcon : undefined
          }
          resolveChosenColor={resolveChosenColor}
          resolveDisplay={resolveDisplay}
          resolveImageUrl={resolveImageUrl}
          shouldShowInMarketBars={settings.shouldShowContentDuration}
          shownKinds={settings.shownKinds}
        />
      </div>

      {itemActions.renderDialogs()}
    </div>
  )
}
WorkspaceMonthCalendar.displayName = 'Calendar: WorkspaceMonthCalendar'
